import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBook, faFileLines, faFolderOpen, faLayerGroup} from "@fortawesome/free-solid-svg-icons";
import {useUserDashboard} from "../store/user";
import LearnerShell from "../components/learner/LearnerShell";
import Paginator from "../components/learner/Paginator";
import EmptyState from "../components/learner/EmptyState";
import {imageUrl, shortDate} from "../lib/format";

const LIMIT = 10;

const SHORTCUTS = [
  {to: "/content-library", icon: faFolderOpen, label: "My Library", hint: "Everything you own"},
  {to: "/books", icon: faBook, label: "Books", hint: "Browse the catalog"},
  {to: "/journals", icon: faFileLines, label: "Journals", hint: "Papers & periodicals"},
];

/**
 * /learner/resources — GET /api/learners/dashboard/recent-resources.
 */
const LearnerResources = () => {
  const navigate = useNavigate();
  const {recentResources, recentResourcesLoading, recentResourcesError, fetchRecentResources} =
    useUserDashboard();
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({total: 0, totalPages: 1});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await fetchRecentResources({page, limit: LIMIT});
      if (!cancelled && result.success) {
        setMeta({total: result.total || 0, totalPages: result.totalPages || 1});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchRecentResources, page]);

  const items = Array.isArray(recentResources) ? recentResources : [];

  return (
    <LearnerShell
      title="Recent resources"
      subtitle="Newest materials added for learners — courses, books and journals."
      icon={faLayerGroup}
    >
      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        {SHORTCUTS.map(({to, icon, label, hint}) => (
          <button
            key={to}
            type="button"
            onClick={() => navigate(to)}
            className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50/40"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-900 text-white">
              <FontAwesomeIcon icon={icon} />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-slate-900">{label}</span>
              <span className="block text-xs text-slate-500">{hint}</span>
            </span>
          </button>
        ))}
      </div>

      {recentResourcesError ? (
        <p className="m-0 mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{recentResourcesError}</p>
      ) : null}

      {recentResourcesLoading && items.length === 0 ? (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="h-20 animate-pulse rounded-xl border border-slate-200 bg-white" />
          ))}
        </div>
      ) : items.length > 0 ? (
        <>
          <div className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
            {items.map((item) => {
              const cover = imageUrl(item.coverImage || item.image);
              const kind = String(item.type || item.resourceType || "resource").toLowerCase();
              return (
                <div key={item.id || item.title} className="flex items-center gap-4 p-4">
                  {cover ? (
                    <img
                      src={cover}
                      alt={item.title || "Resource"}
                      loading="lazy"
                      className="h-14 w-14 shrink-0 rounded-lg border border-slate-200 object-cover"
                    />
                  ) : (
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-400">
                      <FontAwesomeIcon icon={faLayerGroup} />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="m-0 truncate text-sm font-semibold text-slate-900">
                      {item.title || item.course?.title || "Untitled resource"}
                    </p>
                    <p className="m-0 mt-0.5 truncate text-xs text-slate-500">
                      {[kind, item.course?.title, shortDate(item.createdAt || item.publishedAt)]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          <Paginator page={page} totalPages={meta.totalPages} onChange={setPage} />
        </>
      ) : (
        <EmptyState
          icon={faLayerGroup}
          title="No recent resources"
          hint="Freshly published courses, books and journals will show up here."
        />
      )}
    </LearnerShell>
  );
};

export default LearnerResources;
